// Dependencies
import Vue from 'vue';
import get from 'lodash/get';
import merge from 'lodash/merge';

// Calc Utils
import {
    getChipDistribution,
    getTournamentPayouts,
    getBlindStructure
} from '../utils/calculator-utils';

// Calc Data
import {
    firstPayoutObj,
    lastPayoutObj
} from '../utils/calculator-data'

// Config Objects/Data
import {
    payoutStructuresEnum,
    defaultPayoutStructure,
    defaultBuyIn,
    defaultNumberOfPlayers,
    tournamentLengthsEnum,
    defaultTournamentLength,
    blindLevelsEnum,
    defaultBlindLevel,
    numberOfChipsEnum,
    defaultNumberOfChips
} from '../config/form';
import allTranslations from '../config/translations';

// Types
import {
    formElementVariants,
    formElementTypes
} from '../types/FormElement.types';

export default {
    namespaced: true,
    state: {
        // Locales
        currency: 'USD',
        language: 'en-US',
        translations: {},

        // Style Classes
        cssClasses: {},

        // Form loading indicator
        formLoadingTime: 700, // in ms
        formLoadingStatus: false,
        
        /**
         * State for scroll events.
         * In scroll to results case new Vue instance is used for the event bus pattern.
         * More about event bus: https://alligator.io/vuejs/global-event-bus/
         */
        scrollOnCalculateEventName: 'event-scroll',
        scrollOnCalculateInstance: new Vue(),
        scrollOnCalculateMarginOfError: 40,
        smoothScrollAvailable: 'scrollBehavior' in document.documentElement.style,

        /**
         * Form Fields Data
         * @typedef {Object} formFieldObject - each object of form field
         * @property {String} variant - input field variant (e.g. input/select/radio)
         * @property {String} type - the data type, that defines value format (e.g. number, text, date)
         * @property {Object} [options] - (optional) Options data for select variant
         * @property {Boolean} [divider] - (optional) Adds divider below if value exist 
         * @property {[String | Number]} defaultValue - Default field value
         * @property {[String | Number]} editableValue - Form value, bound to field while editing
         * @property {[String | Number]} submittedValue - Value used to calculate results
         * @property {Object} [attr] - (optional) Form field DOM attributes
         * @property {Object} validity - Validation object, based on ValidityState API (https://developer.mozilla.org/en-US/docs/Web/API/ValidityState) 
         */
        formFieldsData: {
            payoutStructure: {
                variant: formElementVariants.select,
                options: payoutStructuresEnum,
                defaultValue: defaultPayoutStructure,
                editableValue: defaultPayoutStructure,
                submittedValue: defaultPayoutStructure,
                attr: {
                    required: true,
                },
                validity: {
                    invalid: false
                }
            },
            buyIn: {
                variant: formElementVariants.input,
                type: formElementTypes.number,
                divider: true,
                defaultValue: defaultBuyIn,
                editableValue: defaultBuyIn,
                submittedValue: defaultBuyIn,
                attr: {
                    type: formElementTypes.number,
                    required: true,
                    min: 0,
                },
                validity: {
                    invalid: false,
                    rangeUnderflow: false
                }
            },
            numberOfPlayers: {
                variant: formElementVariants.input,
                type: formElementTypes.number,
                defaultValue: defaultNumberOfPlayers,
                editableValue: defaultNumberOfPlayers,
                submittedValue: defaultNumberOfPlayers,
                attr: {
                    type: formElementTypes.number,
                    required: true,
                    min: firstPayoutObj.rangeOfPlayers.min,
                    max: lastPayoutObj.rangeOfPlayers.max,
                },
                validity: {
                    invalid: false,
                    rangeUnderflow: false,
                    rangeOverflow: false,
                }
            },
            tournamentLength: {
                variant: formElementVariants.select,
                type: formElementTypes.number,
                options: tournamentLengthsEnum,
                defaultValue: defaultTournamentLength,
                editableValue: defaultTournamentLength,
                submittedValue: defaultTournamentLength,
                attr: {
                    required: true,
                },
                validity: {
                    invalid: false,
                }
            },
            blindLevel: {
                variant: formElementVariants.select,
                type: formElementTypes.number,
                options: blindLevelsEnum,
                defaultValue: defaultBlindLevel,
                editableValue: defaultBlindLevel,
                submittedValue: defaultBlindLevel,
                attr: {
                    required: true,
                },
                validity: {
                    invalid: false,
                }
            },
            numberOfChips: {
                variant: formElementVariants.select,
                type: formElementTypes.number,
                options: numberOfChipsEnum,
                defaultValue: defaultNumberOfChips,
                editableValue: defaultNumberOfChips,
                submittedValue: defaultNumberOfChips,
                attr: {
                    required: true,
                },
                validity: {
                    invalid: false,
                }
            }
        }
    },
    mutations: {
        // Locale Mutations
        SET_LANGUAGE(state, language) {
            state.language = language;
        },
        SET_CURRENCY(state, currency) {
            state.currency = currency;
        },
        SET_TRANSLATIONS(state, translations) {
            state.translations = translations;
        },
        // Style Classes Mutations
        SET_CSS_CLASSES(state, cssClasses) {
            state.cssClasses = cssClasses;
        },
        // Form Mutations
        SET_EDITABLE_FORM_VALUE(state, { fieldName, newValue }) {
            state.formFieldsData[fieldName].editableValue = newValue;
        },
        SET_SUBMITTED_FORM_VALUES(state, formEntries) {
            formEntries.forEach(
                ([fieldName, fieldValue]) => {
                    const isNumeric = state.formFieldsData[fieldName].type === formElementTypes.number;
                    const newValue = isNumeric ? Number(fieldValue) : fieldValue;
                    state.formFieldsData[fieldName].submittedValue = newValue;
                }
            )
        },
        SET_FORM_FIELD_VALIDITY(state, { fieldName, validity }) {
            const formFieldValidity = state.formFieldsData[fieldName].validity;
            Object
                .keys(formFieldValidity)
                .forEach(validityKey => {
                    state.formFieldsData[fieldName].validity[validityKey] = validity[validityKey]
                });
        },
        RESET_EDITABLE_FORM_VALUES_TO_SUBMITTED(state) {
            Object.keys(state.formFieldsData).forEach(
                fieldName => {
                    const { submittedValue } = state.formFieldsData[fieldName]; 
                    state.formFieldsData[fieldName].editableValue = submittedValue;
                }
            );
        },
        RESET_FORM_VALUES_TO_DEFAULT(state) {
            Object.keys(state.formFieldsData).forEach(
                fieldName => {
                    const { defaultValue } = state.formFieldsData[fieldName]; 
                    state.formFieldsData[fieldName].editableValue = defaultValue;
                    state.formFieldsData[fieldName].submittedValue = defaultValue;
                }
            );
        },
        RESET_FORM_FILEDS_VALIDITY(state) {
            Object.keys(state.formFieldsData).forEach(
                fieldName => {
                    const { validity } = state.formFieldsData[fieldName]; 
                    for (let validityKey in validity) {
                        state.formFieldsData[fieldName].validity[validityKey] = false;
                    }
                }
            );
        },
        // Form loading indicator Mutations
        TOGGLE_FORM_LOADING_STATUS(state) {
            state.formLoadingStatus = !state.formLoadingStatus;
        }
    },
    actions: {
        // Locale Actions
        setLanguage({ commit }, language) {
            commit('SET_LANGUAGE', language);
        },
        setCurrency({ commit }, currency) {
            commit('SET_CURRENCY', currency);
        },
        setTranslations({ commit, state }, translations) {
            let defaultTranslations = {};

            const language = state.language.substring(0, 2);
            const avaiableLanguages = Object.keys(allTranslations);

            if (avaiableLanguages.includes(language)) {
                defaultTranslations = allTranslations[language];
            } else {
                defaultTranslations = allTranslations.en;
            }

            commit(
                'SET_TRANSLATIONS',
                merge(defaultTranslations, translations)
            );
        },
        // Style Classes Actions
        setCssClasses({ commit }, cssClasses) {
            commit('SET_CSS_CLASSES', cssClasses);
        },
        // Form Actions
        setEditableFormValue({ commit }, payload) {
            commit('SET_EDITABLE_FORM_VALUE', payload);
        },
        setSubmittedFormValues({ commit, getters }) {
            const formEntries = Object.entries(getters.formFieldsObject('editableValue'));
            commit('SET_SUBMITTED_FORM_VALUES', formEntries);
        },
        setFormFieldValidity({ commit }, payload) {
            commit('SET_FORM_FIELD_VALIDITY', payload);
        },
        resetForm({ commit }) {
            commit('RESET_FORM_VALUES_TO_DEFAULT');
            commit('RESET_FORM_FILEDS_VALIDITY');
        },
        resetEditableFormFields({ commit }) {
            commit('RESET_EDITABLE_FORM_VALUES_TO_SUBMITTED');
        },
        resetFormFieldsValidity({ commit }) {
            commit('RESET_FORM_FILEDS_VALIDITY');
        },
        // Form Loading indicatior Actions
        initFormLoadingIndicator({ commit, state }, callbackFn) {
            const toggleStatus = () => commit('TOGGLE_FORM_LOADING_STATUS');
            const loadingTimePromise = () => new Promise(resolve => { setTimeout(resolve, state.formLoadingTime) })

            return Promise
                .resolve()
                .then(toggleStatus)
                .then(loadingTimePromise)
                .then(callbackFn)
                .then(toggleStatus)
        }
    },
    getters: {
        // Translations Getters
        t: state => key => {
            return get(state.translations, key);
        },
        // Form Getters
        formFieldsObject: state => fieldType => {
           return Object
            .entries(state.formFieldsData)
            .reduce(
                (valuesObj, [fieldName, fieldData]) => {
                    return {
                        ...valuesObj,
                        [fieldName]: fieldData[fieldType]
                    }
                },
            {})
       },
       formFieldsSubmittedObj: (state, getters) => {
            return getters.formFieldsObject('submittedValue');
       },
       formFieldsSubmittedValues: (state, getters) => {
            return Object.values(getters.formFieldsObject('submittedValue')).map(String);
       },
       formFieldsEditableValues: (state, getters) => {
            return Object.values(getters.formFieldsObject('editableValue')).map(String);
       },
       formFieldsDefaultValues: (state, getters) => {
            return Object.values(getters.formFieldsObject('defaultValue')).map(String);
       },
       editableEqualToSubmitted: (state, getters) => {
            const { formFieldsEditableValues, formFieldsSubmittedValues } = getters;
            return formFieldsEditableValues.every((value, index) => value === formFieldsSubmittedValues[index]);
       },
       defaultEqualToSubmitted: (state, getters) => {
            const { formFieldsDefaultValues, formFieldsSubmittedValues } = getters;
            return formFieldsDefaultValues.every((value, index) => value === formFieldsSubmittedValues[index]);
       },
       blindStructure: (state, getters) => {
            const { formFieldsSubmittedObj } = getters;
            return getBlindStructure(
                formFieldsSubmittedObj.blindLevel,
                formFieldsSubmittedObj.numberOfChips,
                formFieldsSubmittedObj.tournamentLength,
            );
        },
        chipDistribution: (state, getters) => {
            const { formFieldsSubmittedObj } = getters;
            return getChipDistribution(
                formFieldsSubmittedObj.numberOfChips,
                formFieldsSubmittedObj.numberOfPlayers
            );
        },
        tournamentPayouts: (state, getters) => {
            const { formFieldsSubmittedObj } = getters;
            return getTournamentPayouts(
                formFieldsSubmittedObj.buyIn,
                formFieldsSubmittedObj.numberOfPlayers,
                formFieldsSubmittedObj.payoutStructure,
            );
        },
        dataForDownloadableDoc: (state, getters) => {
            return {
                selections: getters.formFieldsSubmittedObj,
                tournamentPayouts: getters.tournamentPayouts,
                chipDistribution: getters.chipDistribution,
                blindStructure: getters.blindStructure,
            };
        }
    },
    namespaced: true,
};
